import {
  keepPreviousData,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useEffect, useId, useMemo, useState } from "react";
import {
  Loader2,
  Maximize2,
  MessageCircleQuestion,
  Pencil,
  RotateCw,
  Save,
  Search,
  Tag,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "sonner";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  queryClient,
} from "@/lib/api";
import { loadAllReceipts } from "@/lib/receipt-pages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BulkActionBar } from "@/components/shared/bulk-action-bar";
import { CategoryPicker } from "@/components/shared/category-picker";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmailComposer } from "@/components/shared/email-composer";
import { QueryError } from "@/components/shared/query-error";
import { RecategorizeDialog } from "@/components/shared/recategorize-dialog";
import {
  SortableHeader,
  type SortDir,
} from "@/components/shared/sortable-header";
import { Checkbox } from "@/components/ui/checkbox";
import { useBulkSelection } from "@/hooks/use-bulk-selection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  fiscalYearFromConfig,
  resolvePeriod,
  periodOptions,
  type PeriodPreset,
} from "@/lib/period-presets";
import {
  countryConfigOrDefault,
  formatAmount,
  taxLabel,
  totalTaxLabel,
  vendorTaxIdLabel,
  type CountryConfig,
} from "@/lib/country";
import { cn } from "@/lib/utils";

// The receipts endpoint serves at most 100 rows per request.
const RECEIPTS_PAGE_SIZE = 100;
// Beyond this the table asks the accountant to narrow the filters rather than
// render thousands of rows.
const RECEIPTS_MAX_ROWS = 500;

type SortField =
  | "date"
  | "total"
  | "tax"
  | "vendor_name"
  | "category_name"
  | "uploader"
  | "created";

type BreakdownSortField = "category" | "count" | "total" | "tax" | "pct";
// SortableHeader moved to components/shared/sortable-header.tsx so the
// clients table can share the same sort affordance.

function formatCategoryName(name: string): string {
  if (!name) return name;
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPdfPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return /\.pdf(\?|$)/i.test(path);
}

interface ReceiptItem {
  id: string;
  file_path: string;
  presigned_url: string;
  confirmed: boolean;
  has_raw_data: boolean;
  total: string | null;
  tax: string | null;
  date: string | null;
  vendor_name: string;
  vendor_tax_id: string;
  note: string;
  category_name: string;
  uploaded_by_email: string | null;
  created: string;
}

interface CategoryBreakdown {
  category_id: string | null;
  category_name: string | null;
  count: number;
  total: string;
  gst: string;
  pct_of_spend: number;
}

interface ReceiptListResponse {
  results: ReceiptItem[];
  total_count: number;
  has_more: boolean;
  processed_count: number;
  total_spend: string;
  total_gst: string;
  avg_receipt: string | null;
  by_category: CategoryBreakdown[];
}


export function ConfirmedReceiptsTable({
  userId,
  categories = [],
  clientName = "Client",
  clientEmail,
  countryConfig,
  onPeriodChange,
}: {
  userId: string;
  categories?: { id: string; name: string }[];
  clientName?: string;
  clientEmail?: string;
  /** Client's country config — drives tax wording and the fiscal-year presets. */
  countryConfig?: CountryConfig | null;
  /** Notify the parent whenever the user narrows the period. Lets the parent
   *  mirror the same preset into the Export dialog so the two stay in sync. */
  onPeriodChange?: (
    preset: PeriodPreset,
    from: string | null,
    to: string | null,
  ) => void;
}) {
  const country = countryConfigOrDefault(countryConfig);
  const taxName = taxLabel(country);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [uploaderFilter, setUploaderFilter] = useState<"all" | "me" | "client">(
    "all",
  );
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [askOpenFor, setAskOpenFor] = useState<string | null>(null);
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };
  const [editForm, setEditForm] = useState({
    vendor_name: "",
    vendor_tax_id: "",
    total: "",
    tax: "",
    date: "",
    note: "",
    category_name: "",
  });
  const editVendorId = useId();
  const editVendorTaxIdId = useId();
  const editDateId = useId();
  const editTotalId = useId();
  const editTaxId = useId();
  const editCategoryLabelId = useId();
  const editNoteId = useId();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [rowDeleteId, setRowDeleteId] = useState<string | null>(null);

  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const fiscalYear = fiscalYearFromConfig(country);

  // Not manually memoised: the React Compiler cannot preserve a manual memo
  // whose dependency is an object built in this render, and resolvePeriod is
  // pure and cheap enough that recomputing it costs nothing.
  const resolved = resolvePeriod(periodPreset, {
    customFrom,
    customTo,
    fiscalYear,
  });
  const periodDates = { from: resolved.from, to: resolved.to };

  useEffect(() => {
    onPeriodChange?.(periodPreset, resolved.from, resolved.to);
  }, [periodPreset, resolved.from, resolved.to, onPeriodChange]);

  // Sort decision: the backend /receipts endpoint has no `ordering` param, so
  // sorting is client-side (see `visible` useMemo below). sortField/sortDir are
  // intentionally excluded from queryKey — including them would trigger a fresh
  // network request on every column header click, which is wasteful when all
  // the data is already in memory.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "accountant-receipts",
      userId,
      "confirmed",
      search,
      periodDates.from,
      periodDates.to,
      categoryFilter,
      uploaderFilter,
    ],
    // Every page, not just the first: the table sorts and selects in the
    // browser, and a client with more than one page of receipts used to have
    // the older ones silently left out.
    queryFn: () =>
      loadAllReceipts<ReceiptItem, ReceiptListResponse>(
        ({ to, offset }) => {
          const params = new URLSearchParams({
            limit: String(RECEIPTS_PAGE_SIZE),
            offset: String(offset),
            status: "confirmed",
          });
          if (search.trim()) params.set("search", search.trim());
          if (periodDates.from) params.set("from", periodDates.from);
          if (to) params.set("to", to);
          if (categoryFilter && categoryFilter !== "all") params.set("category", categoryFilter);
          if (uploaderFilter && uploaderFilter !== "all") params.set("uploader", uploaderFilter);
          return apiGet<ReceiptListResponse>(
            `/api/accountant/users/${userId}/receipts?${params}`,
          );
        },
        {
          to: periodDates.to,
          hasDateBounds: !!(periodDates.from || periodDates.to),
          maxRows: RECEIPTS_MAX_ROWS,
        },
      ),
    // Keep the previous page of results rendered while a new search is in
    // flight. Without this the query key change flips `isLoading` back to
    // true, which replaces the whole table with a Skeleton and unmounts the
    // search input — knocking focus off after every keystroke.
    placeholderData: keepPreviousData,
  });

  const receipts = useMemo(() => data?.results ?? [], [data]);
  const selected = receipts.find((r) => r.id === selectedId);

  const uploaderOfReceipt = (r: ReceiptItem): "me" | "client" =>
    r.uploaded_by_email && r.uploaded_by_email !== "" ? "me" : "client";

  const visible = useMemo(() => {
    const out = receipts.slice();
    out.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      let va: string | number = "";
      let vb: string | number = "";
      if (sortField === "total") {
        va = Number(a.total ?? 0);
        vb = Number(b.total ?? 0);
      } else if (sortField === "tax") {
        va = Number(a.tax ?? 0);
        vb = Number(b.tax ?? 0);
      } else if (sortField === "uploader") {
        // Same rule as uploaderOfReceipt: an uploader email means "You".
        va = a.uploaded_by_email ? "me" : "client";
        vb = b.uploaded_by_email ? "me" : "client";
      } else if (sortField === "date") {
        va = a.date ?? "";
        vb = b.date ?? "";
      } else if (sortField === "vendor_name") {
        va = (a.vendor_name || "").toLowerCase();
        vb = (b.vendor_name || "").toLowerCase();
      } else if (sortField === "category_name") {
        va = (a.category_name || "").toLowerCase();
        vb = (b.category_name || "").toLowerCase();
      } else {
        va = a.created ?? "";
        vb = b.created ?? "";
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return out;
  }, [receipts, sortField, sortDir]);

  // Category breakdown: same click-to-sort headers as the receipts table.
  // Default matches what the API returns (largest total first).
  const [breakdownSortField, setBreakdownSortField] =
    useState<BreakdownSortField>("total");
  const [breakdownSortDir, setBreakdownSortDir] = useState<SortDir>("desc");
  const toggleBreakdownSort = (field: BreakdownSortField) => {
    if (breakdownSortField === field) {
      setBreakdownSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setBreakdownSortField(field);
      setBreakdownSortDir(field === "category" ? "asc" : "desc");
    }
  };
  const sortedBreakdown = useMemo(() => {
    const rows = (data?.by_category ?? []).slice();
    const dir = breakdownSortDir === "asc" ? 1 : -1;
    const key = (cat: CategoryBreakdown): string | number => {
      if (breakdownSortField === "category") {
        return (cat.category_name ?? "").toLowerCase();
      }
      if (breakdownSortField === "count") return cat.count;
      if (breakdownSortField === "tax") return Number(cat.gst);
      if (breakdownSortField === "pct") return cat.pct_of_spend;
      return Number(cat.total);
    };
    rows.sort((a, b) => {
      const va = key(a);
      const vb = key(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return rows;
  }, [data, breakdownSortField, breakdownSortDir]);

  const bulk = useBulkSelection({ items: visible, getId: (r) => r.id });
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [recategorizeOpen, setRecategorizeOpen] = useState(false);

  // P3: Stable category dropdown options. We used to compute these from the
  // currently-visible receipts, which meant narrowing by Fuel turned the
  // dropdown into a single "Fuel" entry (and base-ui Select would silently
  // reset categoryFilter when its value fell out of the option set as other
  // filters changed). Use the accountant's full list, plus whatever the
  // current filter is (in case the receipt uses a client-created category
  // that the accountant hasn't configured).
  const stableCategoryOptions = useMemo(() => {
    const names = new Set<string>();
    for (const c of categories) if (c.name) names.add(c.name);
    if (categoryFilter && categoryFilter !== "all") names.add(categoryFilter);
    return Array.from(names).sort();
  }, [categories, categoryFilter]);

  const askMutation = useMutation({
    mutationFn: ({
      receiptId,
      message,
      subject,
    }: {
      receiptId: string;
      message: string;
      subject?: string;
    }) =>
      apiPost(
        `/api/accountant/users/${userId}/receipts/${receiptId}/query`,
        { message, subject },
      ),
    onSuccess: () => {
      toast.success("Question sent to client");
      queryClient.invalidateQueries({ queryKey: ["accountant-messages", userId] });
      setAskOpenFor(null);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not send"),
  });

  // Reset on id change during render (React's "reset state when prop changes" pattern).
  if (selectedId !== lastSelectedId) {
    setLastSelectedId(selectedId);
    setZoom(1);
    setRotation(0);
    if (selected) {
      setEditForm({
        vendor_name: selected.vendor_name || "",
        vendor_tax_id: selected.vendor_tax_id || "",
        total: selected.total || "",
        tax: selected.tax || "",
        date: selected.date || "",
        note: selected.note || "",
        category_name: selected.category_name || "",
      });
    }
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      // DRF's DateField rejects empty strings; strip blanks before PATCH.
      const payload: Record<string, string> = {};
      for (const [k, v] of Object.entries(editForm)) {
        if (v !== "") payload[k] = v;
      }
      return apiPatch(
        `/api/accountant/users/${userId}/receipts/${selectedId}`,
        payload,
      );
    },
    onSuccess: () => {
      toast.success("Changes saved");
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
      setSelectedId(null);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not save"),
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiDelete(`/api/accountant/users/${userId}/receipts/${selectedId}`),
    onSuccess: () => {
      toast.success("Receipt deleted");
      setSelectedId(null);
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not delete"),
  });

  const rowDeleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/accountant/users/${userId}/receipts/${id}`),
    onSuccess: () => {
      toast.success("Receipt deleted");
      setRowDeleteId(null);
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not delete"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) =>
      apiPost(
        `/api/accountant/users/${userId}/receipts/batch-delete`,
        { receipt_ids: ids },
      ),
    onSuccess: (_data, ids) => {
      toast.success(
        `${ids.length} receipt${ids.length === 1 ? "" : "s"} deleted`,
      );
      setBulkDeleteOpen(false);
      bulk.clear();
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not delete"),
  });

  const bulkRecategorizeMutation = useMutation({
    mutationFn: ({ ids, category_name }: { ids: string[]; category_name: string }) =>
      apiPatch(
        `/api/accountant/users/${userId}/receipts/batch-update`,
        { receipt_ids: ids, category_name },
      ),
    onSuccess: (_data, { ids, category_name }) => {
      toast.success(
        `${ids.length} receipt${ids.length === 1 ? "" : "s"} recategorized as ${formatCategoryName(category_name)}`,
      );
      setRecategorizeOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not recategorize"),
  });

  const queryMutation = useMutation({
    mutationFn: ({
      message,
      subject,
    }: {
      message: string;
      subject?: string;
    }) =>
      apiPost(`/api/accountant/users/${userId}/receipts/${selectedId}/query`, {
        message,
        subject,
      }),
    onSuccess: () => {
      toast.success("Question sent to client");
      queryClient.invalidateQueries({ queryKey: ["accountant-messages", userId] });
      setQueryOpen(false);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not send"),
  });

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) =>
      apiPost("/api/accountant/categories", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accountant-me"] });
    },
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border divide-y">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2.5">
            <Skeleton className="size-10 rounded shrink-0" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <QueryError
        error={error}
        message="Couldn't load receipts"
        onRetry={() =>
          queryClient.invalidateQueries({
            queryKey: ["accountant-receipts", userId],
          })
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Unified filter row — items-start keeps every trigger at the same
          top edge even when the period caption renders below the row. */}
      <div className="flex flex-wrap items-start gap-2">
        <div className="relative flex items-center">
          <Search className="absolute left-2.5 size-3.5 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Search vendor..."
            value={search}
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
            className="w-44 pl-8 pr-7"
            aria-label="Search receipts by vendor"
          />
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch("")}
              className="absolute right-1.5 flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}
        </div>

        <Select
          value={periodPreset}
          onValueChange={(v) => {
            const next = (v as PeriodPreset) ?? "all";
            if (next === "custom" && !customFrom && !customTo) {
              setCustomFrom(resolved.from ?? "");
              setCustomTo(resolved.to ?? "");
            }
            setPeriodPreset(next);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue>{resolved.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {periodOptions(undefined, fiscalYear).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <span>{opt.label}</span>
                {opt.caption && (
                  <span className="ml-1.5 text-[11px] text-muted-foreground">
                    {opt.caption}
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={categoryFilter}
          onValueChange={(v) => setCategoryFilter(v ?? "all")}
        >
          <SelectTrigger className="w-40">
            <SelectValue>
              {categoryFilter === "all"
                ? "All categories"
                : formatCategoryName(categoryFilter)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {stableCategoryOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {formatCategoryName(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={uploaderFilter}
          onValueChange={(v) =>
            setUploaderFilter((v as "all" | "me" | "client") ?? "all")
          }
        >
          <SelectTrigger className="min-w-[11rem]">
            <SelectValue>
              {uploaderFilter === "all"
                ? "Any uploader"
                : uploaderFilter === "me"
                  ? "Uploaded by me"
                  : "Uploaded by client"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any uploader</SelectItem>
            <SelectItem value="me">Uploaded by me</SelectItem>
            <SelectItem value="client">Uploaded by client</SelectItem>
          </SelectContent>
        </Select>

        {(search ||
          categoryFilter !== "all" ||
          uploaderFilter !== "all" ||
          periodPreset !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setCategoryFilter("all");
              setUploaderFilter("all");
              setPeriodPreset("all");
              setCustomFrom("");
              setCustomTo("");
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* P1: period date caption sits on its own row so it doesn't push the
          period Select up relative to the other filter triggers. */}
      {resolved.caption && periodPreset !== "custom" && (
        <p className="text-[11px] text-muted-foreground -mt-1">
          {resolved.caption}
        </p>
      )}

      {/* Custom date range — second row, only when preset === "custom" */}
      {periodPreset === "custom" && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={customFrom}
            onChange={(e) =>
              setCustomFrom((e.target as HTMLInputElement).value)
            }
            className="w-36"
            aria-label="From date"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="date"
            value={customTo}
            onChange={(e) =>
              setCustomTo((e.target as HTMLInputElement).value)
            }
            className="w-36"
            aria-label="To date"
          />
        </div>
      )}

      {/* Summary strip — always visible once data is loaded */}
      {isLoading ? (
        <div className="flex gap-6 rounded-lg border px-4 py-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-6 rounded-lg border px-4 py-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Receipts</div>
            <div className="font-medium tabular-nums">
              {data?.total_count ?? 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total spend</div>
            <div className="font-medium tabular-nums">
              {formatAmount(country, data?.total_spend ?? "0.00")}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{totalTaxLabel(country)}</div>
            <div className="font-medium tabular-nums">
              {formatAmount(country, data?.total_gst ?? "0.00")}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Average receipt</div>
            <div className="font-medium tabular-nums">
              {data?.avg_receipt ? formatAmount(country, data.avg_receipt) : "-"}
            </div>
          </div>
        </div>
      )}

      {/* Receipts table + Category breakdown tabs */}
      <Tabs defaultValue="receipts">
        <TabsList>
          <TabsTrigger value="receipts">Receipts</TabsTrigger>
          <TabsTrigger value="breakdown">Category breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="breakdown">
          {(data?.by_category?.length ?? 0) > 0 ? (
            <div className="mt-2 rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      field="category"
                      label="Category"
                      sortField={breakdownSortField}
                      sortDir={breakdownSortDir}
                      onClick={toggleBreakdownSort}
                    />
                    <SortableHeader
                      field="count"
                      label="Count"
                      sortField={breakdownSortField}
                      sortDir={breakdownSortDir}
                      onClick={toggleBreakdownSort}
                      align="right"
                    />
                    <SortableHeader
                      field="total"
                      label="Total"
                      sortField={breakdownSortField}
                      sortDir={breakdownSortDir}
                      onClick={toggleBreakdownSort}
                      align="right"
                    />
                    <SortableHeader
                      field="tax"
                      label={taxName}
                      sortField={breakdownSortField}
                      sortDir={breakdownSortDir}
                      onClick={toggleBreakdownSort}
                      align="right"
                    />
                    <SortableHeader
                      field="pct"
                      label="% of spend"
                      sortField={breakdownSortField}
                      sortDir={breakdownSortDir}
                      onClick={toggleBreakdownSort}
                      align="right"
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBreakdown.map((cat, i) => (
                    <TableRow key={cat.category_id ?? `uncategorised-${i}`}>
                      <TableCell className="font-medium">
                        {cat.category_name
                          ? formatCategoryName(cat.category_name)
                          : "Uncategorised"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cat.count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatAmount(country, cat.total)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatAmount(country, cat.gst)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {cat.pct_of_spend.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-lg border py-8 text-center text-sm text-muted-foreground">
              No category breakdown available.
            </div>
          )}
        </TabsContent>

        <TabsContent value="receipts">
          {/* Reserve the bar's space via a grid-rows transition so selecting a
              row doesn't shove the table down. 0fr → 1fr animates the height
              smoothly; overflow-hidden keeps the bar clipped at 0fr. */}
          <div
            className={cn(
              "grid transition-[grid-template-rows,margin] duration-200 ease-out",
              bulk.someSelected ? "grid-rows-[1fr] mb-3" : "grid-rows-[0fr] mb-0",
            )}
            aria-hidden={!bulk.someSelected}
          >
            <div className="overflow-hidden">
              <BulkActionBar count={bulk.count} onDeselectAll={bulk.clear}>
                <Button
                  variant="destructive"
                  size="xs"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="cursor-pointer"
                  disabled={!bulk.someSelected}
                >
                  <Trash2 className="mr-1 size-3" aria-hidden />
                  Delete selected
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setRecategorizeOpen(true)}
                  className="cursor-pointer"
                  disabled={!bulk.someSelected}
                >
                  <Tag className="mr-1 size-3" aria-hidden />
                  Recategorize
                </Button>
              </BulkActionBar>
            </div>
          </div>
          {visible.length === 0 ? (
            <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
              {search || categoryFilter !== "all" || uploaderFilter !== "all" || periodPreset !== "all" ? (
                "No confirmed receipts match the active filters."
              ) : (
                <>
                  <img
                    src="/illustrations/robot-empty.png"
                    alt=""
                    aria-hidden="true"
                    className="mx-auto mb-3 h-28 w-auto opacity-80"
                  />
                  <span>No confirmed receipts yet.</span>
                </>
              )}
            </div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
          {receipts.length < (data?.total_count ?? 0) && (
            <p
              role="status"
              data-testid="receipts-truncated-notice"
              className="border-b bg-muted/40 px-4 py-2 text-xs text-muted-foreground"
            >
              Showing {receipts.length} of {data?.total_count} receipts. Narrow
              the period, category or search to see the rest. Exports include
              every receipt in the period.
            </p>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={bulk.allSelected}
                    onCheckedChange={bulk.toggleAll}
                    aria-label="Select all receipts"
                  />
                </TableHead>
                <TableHead className="w-12"></TableHead>
                <SortableHeader
                  field="vendor_name"
                  label="Vendor"
                  sortField={sortField}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <SortableHeader
                  field="date"
                  label="Date"
                  sortField={sortField}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <SortableHeader
                  field="category_name"
                  label="Category"
                  sortField={sortField}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <SortableHeader
                  field="total"
                  label="Total"
                  sortField={sortField}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  align="right"
                />
                <SortableHeader
                  field="tax"
                  label={taxName}
                  sortField={sortField}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  align="right"
                />
                <TableHead>Note</TableHead>
                <SortableHeader
                  field="uploader"
                  label="Uploader"
                  sortField={sortField}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setSelectedId(r.id)}
                >
                  <TableCell
                    className="w-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={bulk.has(r.id)}
                      onCheckedChange={() => bulk.toggleOne(r.id)}
                      aria-label={`Select receipt from ${r.vendor_name || "Unknown"}`}
                    />
                  </TableCell>
                  <TableCell className="py-2">
                    {isPdfPath(r.file_path) ? (
                      <div className="size-9 rounded bg-muted flex items-center justify-center text-[9px] font-semibold text-muted-foreground">
                        PDF
                      </div>
                    ) : r.presigned_url ? (
                      <img
                        src={r.presigned_url}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        decoding="async"
                        className="size-9 rounded object-cover bg-muted"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.visibility =
                            "hidden";
                        }}
                      />
                    ) : (
                      <div className="size-9 rounded bg-muted" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.vendor_name || (
                      <span className="text-muted-foreground italic">
                        Unknown
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {r.date || "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.category_name
                      ? formatCategoryName(r.category_name)
                      : "-"}
                  </TableCell>
                  <TableCell
                    className="text-right font-mono"
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {r.total ? formatAmount(country, r.total) : "-"}
                  </TableCell>
                  <TableCell
                    className="text-right font-mono text-muted-foreground"
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {r.tax ? formatAmount(country, r.tax) : "-"}
                  </TableCell>
                  <TableCell className="max-w-48 truncate text-muted-foreground">
                    {r.note || ""}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {uploaderOfReceipt(r) === "me" ? "You" : "Client"}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Ask client about this receipt"
                        title="Ask client"
                        onClick={() => setAskOpenFor(r.id)}
                      >
                        <MessageCircleQuestion
                          className="size-3.5"
                          aria-hidden
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Edit receipt"
                        title="Edit"
                        onClick={() => setSelectedId(r.id)}
                      >
                        <Pencil className="size-3.5" aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Delete receipt"
                        title="Delete"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => setRowDeleteId(r.id)}
                      >
                        <Trash2 className="size-3.5" aria-hidden />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Ask-client dialog driven directly from the table row */}
      <Dialog
        open={askOpenFor != null}
        onOpenChange={(open) => {
          if (!open) setAskOpenFor(null);
        }}
      >
        <DialogContent className="max-w-5xl lg:max-w-6xl xl:max-w-7xl">
          <DialogHeader>
            <DialogTitle>Ask client about this receipt</DialogTitle>
            <DialogDescription>
              Your message is sent via email; the client can reply directly.
            </DialogDescription>
          </DialogHeader>
          {askOpenFor && (
            <EmailComposer
              key={askOpenFor}
              toName={clientName}
              toEmail={clientEmail}
              defaultSubject={(() => {
                const r = receipts.find((x) => x.id === askOpenFor);
                return r?.vendor_name
                  ? `Question about a receipt from ${r.vendor_name}`
                  : "Question about a receipt";
              })()}
              initialBody=""
              bodyMode="rich"
              bodyLabel="Question"
              bodyRequired
              bodyPlaceholder="e.g. Is this for the business or personal?"
              previewType="ask_receipt"
              clientId={userId}
              receiptId={askOpenFor}
              sending={askMutation.isPending}
              onSend={({ subject, body }) =>
                askMutation.mutate({
                  receiptId: askOpenFor,
                  message: body,
                  subject,
                })
              }
              onCancel={() => setAskOpenFor(null)}
              sendLabel="Send question"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog (click-to-edit slide-over equivalent) */}
      <Dialog
        open={selected != null}
        onOpenChange={(open) => !open && setSelectedId(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit confirmed receipt</DialogTitle>
            <DialogDescription>
              Changes here update the details but keep the receipt confirmed.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
              {/* Image */}
              <div className="rounded-lg border bg-muted/30 overflow-hidden">
                <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-muted/50">
                  {/* Zoom / rotate are CSS transforms on <img> — skip them
                      for PDFs (their iframe viewer has built-in controls). */}
                  {!isPdfPath(selected.file_path) && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Zoom in"
                        onClick={() => setZoom((z) => Math.min(z + 0.5, 3))}
                      >
                        <ZoomIn className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Zoom out"
                        onClick={() => setZoom((z) => Math.max(z - 0.5, 0.5))}
                      >
                        <ZoomOut className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Rotate"
                        onClick={() => setRotation((r) => (r + 90) % 360)}
                      >
                        <RotateCw className="size-3.5" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Fullscreen"
                    className="ml-auto"
                    onClick={() => setFullscreen(true)}
                  >
                    <Maximize2 className="size-3.5" />
                  </Button>
                </div>
                <div className="overflow-auto max-h-72 flex items-center justify-center">
                  {isPdfPath(selected.file_path) ? (
                    <iframe
                      key={selected.id}
                      src={selected.presigned_url}
                      title="Receipt PDF"
                      className="w-full h-72 rounded bg-background"
                    />
                  ) : (
                    <img
                      src={selected.presigned_url}
                      alt="Receipt"
                      className="max-w-full object-contain transition-transform"
                      style={{
                        transform: `scale(${zoom}) rotate(${rotation}deg)`,
                      }}
                      onError={(e) =>
                        ((e.target as HTMLImageElement).style.display = "none")
                      }
                    />
                  )}
                </div>
              </div>

              {/* Form */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={editVendorId} className="text-xs">Vendor</Label>
                    <Input
                      id={editVendorId}
                      value={editForm.vendor_name}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          vendor_name: (e.target as HTMLInputElement).value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={editDateId} className="text-xs">Date</Label>
                    <Input
                      id={editDateId}
                      type="date"
                      value={editForm.date}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          date: (e.target as HTMLInputElement).value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label htmlFor={editVendorTaxIdId} className="text-xs">{vendorTaxIdLabel(country)}</Label>
                    <Input
                      id={editVendorTaxIdId}
                      value={editForm.vendor_tax_id}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          vendor_tax_id: (e.target as HTMLInputElement).value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={editTotalId} className="text-xs">Total</Label>
                    <Input
                      id={editTotalId}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={editForm.total}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          total: (e.target as HTMLInputElement).value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={editTaxId} className="text-xs">{taxName}</Label>
                    <Input
                      id={editTaxId}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={editForm.tax}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          tax: (e.target as HTMLInputElement).value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label id={editCategoryLabelId} className="text-xs">Category</Label>
                  <CategoryPicker
                    value={editForm.category_name}
                    onChange={(name) =>
                      setEditForm((f) => ({ ...f, category_name: name }))
                    }
                    categories={categories}
                    onCreateCategory={(name) =>
                      createCategoryMutation.mutate(name)
                    }
                    ariaLabelledBy={editCategoryLabelId}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={editNoteId} className="text-xs">Note</Label>
                  <Textarea
                    id={editNoteId}
                    value={editForm.note}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        note: (e.target as HTMLTextAreaElement).value,
                      }))
                    }
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQueryOpen(true)}
            >
              <MessageCircleQuestion className="mr-1.5 size-3" aria-hidden />
              Ask client
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteMutation.isPending}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1.5 size-3" />
              Delete
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setSelectedId(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-1.5 size-3 animate-spin" />
              ) : (
                <Save className="mr-1.5 size-3" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen image / PDF */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[90vw] w-auto p-2 flex items-center justify-center">
          <DialogTitle className="sr-only">Receipt preview</DialogTitle>
          {selected &&
            (isPdfPath(selected.file_path) ? (
              <iframe
                src={selected.presigned_url}
                title="Receipt PDF fullscreen"
                className="w-[88vw] h-[85vh] rounded bg-background"
              />
            ) : (
              <img
                src={selected.presigned_url}
                alt="Receipt fullscreen"
                className="max-w-[88vw] max-h-[85vh] w-auto h-auto object-contain"
                style={{ transform: `rotate(${rotation}deg)` }}
              />
            ))}
        </DialogContent>
      </Dialog>

      {/* F24: in-app delete confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this receipt?"
        description="This can't be undone. The client will see it disappear from their app."
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() =>
          deleteMutation.mutate(undefined, {
            onSuccess: () => setConfirmDelete(false),
          })
        }
      />

      {/* Row-level delete confirmation (no edit panel open) */}
      <ConfirmDialog
        open={rowDeleteId != null}
        onOpenChange={(open) => !open && setRowDeleteId(null)}
        title="Delete this receipt?"
        description="This can't be undone. The client will see it disappear from their app."
        confirmLabel="Delete"
        destructive
        loading={rowDeleteMutation.isPending}
        onConfirm={() => rowDeleteId && rowDeleteMutation.mutate(rowDeleteId)}
      />

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${bulk.count} receipt${bulk.count === 1 ? "" : "s"}?`}
        description="This can't be undone. The client will see them disappear from their app."
        confirmLabel="Delete"
        destructive
        loading={bulkDeleteMutation.isPending}
        onConfirm={() => bulkDeleteMutation.mutate(bulk.ids)}
      />

      {/* Bulk recategorize */}
      <RecategorizeDialog
        open={recategorizeOpen}
        onOpenChange={setRecategorizeOpen}
        count={bulk.count}
        categories={categories}
        loading={bulkRecategorizeMutation.isPending}
        onConfirm={(category_name) =>
          bulkRecategorizeMutation.mutate({ ids: bulk.ids, category_name })
        }
        onCreateCategory={(name) => createCategoryMutation.mutate(name)}
      />

      {/* Ask client — same composer, triggered from within the edit dialog */}
      <Dialog open={queryOpen && !saveMutation.isPending} onOpenChange={setQueryOpen}>
        <DialogContent className="max-w-5xl lg:max-w-6xl xl:max-w-7xl">
          <DialogHeader>
            <DialogTitle>Ask client about this receipt</DialogTitle>
            <DialogDescription>
              Your message is sent via email; the client can reply directly.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <EmailComposer
              key={selected.id}
              toName={clientName}
              toEmail={clientEmail}
              defaultSubject={
                selected.vendor_name
                  ? `Question about a receipt from ${selected.vendor_name}`
                  : "Question about a receipt"
              }
              initialBody=""
              bodyMode="rich"
              bodyLabel="Question"
              bodyRequired
              bodyPlaceholder="e.g. Is this for the business or personal?"
              previewType="ask_receipt"
              clientId={userId}
              receiptId={selected.id}
              sending={queryMutation.isPending}
              onSend={({ subject, body }) =>
                queryMutation.mutate({ message: body, subject })
              }
              onCancel={() => setQueryOpen(false)}
              sendLabel="Send question"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
